import React, { useState, useEffect } from "react";
import { useReitContext } from "../../../../context/reitContext";
import service from "../../../../services/service";
import { safeFormat } from "../../../../utils/financialHelper";
import "./characteristics.less";

interface CharacteristicsRow {
  metric: string;
  values: (string | number)[];
}

interface PropertyData {
  name: { value: string };
  [key: string]: any;
}

const ReitsCharacteristics: React.FC = () => {
  const displayName = "cmp-reits-compare-characteristics";
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState("FY 2024");
  const [comparisonData, setComparisonData] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { selectedProperties } = useReitContext();

const propertyNames = selectedProperties.map((prop) => prop.name).join(",");

useEffect(() => {
  if (!propertyNames) {
    setComparisonData([]);
    return;
  }

  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await service.compareAdjacent(propertyNames);
      // response is already the array
      if (!response || response.length === 0) {
        throw new Error("No data received from API");
      }
      setComparisonData(response);
    } catch (err) {
      console.error("Failed to fetch comparison data:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  fetchComparisonData();
}, [propertyNames]);

  const getAllMetrics = () => {
    if (!comparisonData.length) return [];
    
    return [
      ...new Set(
        comparisonData
          .flatMap(Object.keys)
          .filter(
            (key) =>
              key !== 'isReit' &&
              key !== '_id' &&
              key !== 'PropertyDetails' &&
              key !== 'name' &&
              key !== 'adjacentOF' &&
              key !== 'leasableAreaHistoricalList' &&
              key !== 'address' &&
              key !== 'coordinates' &&
              key !== '__v'
          )
      ),
    ];
  };

const getDisplayValue = (property: PropertyData, key: string): string | number => {
  if (key === "tenants") {
    if (!property[key] || property[key].length === 0) return "---";
    return property[key].map((tenant: any) => tenant.name).join(", ");
  }

  if (key === "isReit") {
    return property[key] ? property[key].toString() : "---";
  }

  if (key === "occupancy") {
    return property[key] ? 
      (property[key].value !== null ? safeFormat(property[key].value) + "%" : "---") : 
      "---";
  }

  const getFormattedValue = (name: string, value: any): string | number => {
    const cleanName = name.replace(/\n/g, " ");

    switch (cleanName) {
      case "Leasable Area (in msf/ Hotel keys in nos./ MW)":
      case "Value Of The Property : Cumulative Area (in ₹ psf)":
      case "Lease Rentals (in ₹ psf)":
      case "YoY Growth In Lease Rentals  (inplace rents ₹ psf) (%)":
      case "Lease Maturity/Expiry Average Maturity (in yrs)":
      case "Value Of The Property : Cumulative Value (₹ Cr.)":
      case "Valuation (₹ Cr.)":
        return safeFormat(value);
      default:
        return value !== null && value !== undefined ? safeFormat(value) : "---";
    }
  };

  if (property[key]) {
    const { key: name, value } = property[key];
    return value === null ? "---" : getFormattedValue(name, value);
  }

  return "---";
};

// Get display name for a metric
const getDisplayKey = (key: string): string => {
  const property = comparisonData.find((p) => p[key]);
  if (property) {
    if (key === "tenants") return "Tenants";
    return property[key].key || key;
  }
  return key;
};


  const years = ["FY 2024", "FY 2023", "FY 2022"];
  const allMetrics = getAllMetrics();

  // Show loading state
  if (loading) {
    return (
      <section className={displayName}>
        <div className={`${displayName}__card`}>
          <div className={`${displayName}__loading`}>
            Loading comparison data...
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={displayName}>
        <div className={`${displayName}__card`}>
          <div className={`${displayName}__error`}>
            Error: {error}
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no properties selected
  if (selectedProperties.length === 0) {
    return (
      <section className={displayName}>
        <div className={`${displayName}__card`}>
          <div className={`${displayName}__empty`}>
            Select properties to compare their characteristics
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={displayName}>
      <div className={`${displayName}__card`}>
        <div className={`${displayName}__table`}>
          <table className={`${displayName}__table-wrapper`}>
            <thead>
              <tr>
                <th className={`${displayName}__table-head-cell`}>Property</th>
                {comparisonData.map((property, index) => (
                  <th className={`${displayName}__table-head-cell`} key={index}>
                    {property.name.value}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allMetrics.map((metric) => (
                <tr key={metric} className={`${displayName}__table-row`}>
                  <td className={`${displayName}__table-cell`}>
                    {getDisplayKey(metric)}
                  </td>
                  {comparisonData.map((property, index) => (
                    <td key={index} className={`${displayName}__table-cell`}>
                      {getDisplayValue(property, metric)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ReitsCharacteristics;